import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import styled from "styled-components";
import HeadingTwo from "../../components/HeadingTwo";
import InfoComponent from "../../components/InfoComponent";
import MultipleSelect from "../../components/MultipleSelect";
import StateInput from "../../components/StateInput";
import StyledButton from "../../components/StyledButton";
import colors from "../../constants/colors";
import constants from "../../constants/constants";
import { GetUserContext } from "../../localStorage/Store";
import styl from "../../styl";
import AboAccordion from "./AboAccordion";

// "1.234,50" -> 1234.5
const parsePrice = (price) =>
  parseFloat((price || "0").replace(/\./g, "").replace(",", "."));

// Anzeigename der Variante, z.B. "3 Monate"
export const getVariationLabel = (variation) =>
  variation.attributes?.nodes?.map((attribute) => attribute.value).join(", ") ||
  variation.name;

const shippingFields = [
  // required: Fehlermeldungen wie in UserData / RegisterPage
  {
    name: "firstName",
    placeholder: "Vorname",
    maxLength: "40",
    required: "Gib einen Vornamen an.",
  },
  {
    name: "lastName",
    placeholder: "Nachname",
    maxLength: "40",
    required: "Gib einen Nachnamen an.",
  },
  {
    name: "address1",
    placeholder: "Adresse",
    maxLength: "110",
    required: "Gib eine Adresse an.",
  },
  {
    name: "postcode",
    placeholder: "PLZ",
    maxLength: "10",
    required: "Gib eine PLZ an.",
  },
  {
    name: "city",
    placeholder: "Stadt",
    maxLength: "100",
    required: "Gib einen Ort an.",
  },
  {
    name: "country",
    placeholder: "Länderkürzel",
    maxLength: "2",
    required: "Gib einen Länderkürzel an.",
  },
];

const getShippingFromUser = (user) =>
  Object.fromEntries(
    shippingFields.map(({ name }) => [name, user?.shipping?.[name] || ""]),
  );

const postcodeFormats = { AT: /^\d{4}$/, DE: /^\d{5}$/ };
const postcodeNotFound =
  "PLZ wurde nicht in Deutschland oder Österreich gefunden.";

const hereCountryCodes = { AT: "AUT", DE: "DEU" };

// Prüft über HERE, ob es die PLZ im angegebenen Land gibt (wie bei Registrierung / Profil)
const postcodeExists = (postcode, country) =>
  new Promise((resolve) => {
    const H = window.H;
    if (!H) return resolve(true);
    const platform = new H.service.Platform({
      apikey: "hzdcbr05flQK6n8sOrnmm_3kROdUdeMN9QiYD2nl-Qc",
    });
    platform.getSearchService().geocode(
      {
        in: `countryCode:${hereCountryCodes[country]}`,
        qq: `postalCode=${postcode}`,
        at: "47.4345111,9.8987025,17",
      },
      (result) => resolve(result.items.length > 0),
      // HERE nicht erreichbar: Bestellung nicht blockieren, Format wurde bereits geprüft
      () => resolve(true),
    );
  });

const validateShipping = async (shipping) => {
  const errors = {};
  shippingFields.forEach(({ name, required }) => {
    if (!shipping[name]?.trim()) errors[name] = required;
  });
  const country = shipping.country.trim().toUpperCase();
  if (!errors.country && !postcodeFormats[country]) {
    errors.country = "Nur AT oder DE möglich";
  }
  if (!errors.country && !errors.postcode) {
    const postcode = shipping.postcode.trim();
    if (
      !postcodeFormats[country].test(postcode) ||
      !(await postcodeExists(postcode, country))
    ) {
      errors.postcode = postcodeNotFound;
    }
  }
  return errors;
};

/**
 * @author Sandro Mennel
 * @param {object} product the abo product from the backend (required)
 * @param {number} initialVariationId variation to preselect, e.g. after login
 * @param {function} onOrder called with (product, variation, shippingAddress) when ordering (required). shippingAddress is null if the address from the user profile should be used
 * @param {boolean} loading shows the loading state on the order button
 * @param {boolean} loggedIn shows login / register links when false
 * @example <AboProduct product={product} onOrder={(product, variation) => {}} />
 */
export default function AboProduct({
  product,
  initialVariationId,
  onOrder,
  loading,
  loggedIn,
}) {
  const variations = [...(product.variations?.nodes || [])].sort(
    (a, b) => parsePrice(a.price) - parsePrice(b.price),
  );
  // Bilder, die nicht geladen werden können (z.B. 404), werden ausgeblendet
  const [brokenImages, setBrokenImages] = useState([]);
  const images = [
    product.image,
    ...(product.galleryImages?.nodes || []),
  ].filter(
    (image) => image?.sourceUrl && !brokenImages.includes(image.sourceUrl),
  );
  const handleImageError = (sourceUrl) =>
    setBrokenImages((prev) => [...prev, sourceUrl]);

  const [selectedVariationId, setSelectedVariationId] = useState(
    variations.some((v) => v.variationId === initialVariationId)
      ? initialVariationId
      : variations[0]?.variationId,
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [user] = useContext(GetUserContext);
  // Abweichende Lieferadresse nur für diese Abo-Bestellung (null = Adresse aus dem Profil)
  const [customShipping, setCustomShipping] = useState(null);
  const [shippingErrors, setShippingErrors] = useState({});
  const [validating, setValidating] = useState(false);

  const currentImage = images[Math.min(selectedImage, images.length - 1)];

  // Versandinformationen werden im Backend im Feld "Herkunft" gepflegt
  const shippingInfo = product.e3_origin?.herkunft?.trim();

  const selectedVariation = variations.find(
    (v) => v.variationId === selectedVariationId,
  );

  // Nach Login / Registrierung zurück zum Abo mit der gewählten Laufzeit
  const redirectQuery =
    "?redirect=" +
    encodeURIComponent(`/abo?variation=${selectedVariationId ?? ""}`);

  const toggleCustomShipping = (selected) => {
    setShippingErrors({});
    setCustomShipping(selected.length > 0 ? getShippingFromUser(user) : null);
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setCustomShipping((prev) => ({ ...prev, [name]: value }));
    setShippingErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleOrder = async () => {
    if (!selectedVariation || validating) return;
    if (customShipping) {
      setValidating(true);
      const errors = await validateShipping(customShipping);
      setValidating(false);
      setShippingErrors(errors);
      if (Object.keys(errors).length > 0) return;
    }
    onOrder(
      product,
      selectedVariation,
      customShipping
        ? {
            ...customShipping,
            postcode: customShipping.postcode.trim(),
            country: customShipping.country.trim().toUpperCase(),
          }
        : null,
    );
  };

  return (
    <Main>
      <Gallery>
        {currentImage && (
          <MainImage
            src={currentImage.sourceUrl}
            alt={currentImage.altText || product.name}
            onError={() => handleImageError(currentImage.sourceUrl)}
          />
        )}
        {images.length > 1 && (
          <Thumbnails>
            {images.map((image, i) => (
              <Thumbnail
                key={image.sourceUrl + i}
                src={image.sourceUrl}
                alt={image.altText || product.name}
                $selected={image === currentImage}
                onClick={() => setSelectedImage(i)}
                onError={() => handleImageError(image.sourceUrl)}
              />
            ))}
          </Thumbnails>
        )}
      </Gallery>

      <Info>
        <HeadingTwo title={product.name} />
        {selectedVariation && <Price>€ {selectedVariation.price}</Price>}

        {variations.length > 0 && (
          <>
            <LabelRow>
              <Label>Laufzeit</Label>
              {shippingInfo && (
                <InfoComponent text="Details in den Versandinformationen" />
              )}
            </LabelRow>
            <Options>
              {variations.map((variation) => (
                <Option
                  key={variation.variationId}
                  $selected={variation.variationId === selectedVariationId}
                  onClick={() => setSelectedVariationId(variation.variationId)}
                >
                  {getVariationLabel(variation)}
                </Option>
              ))}
            </Options>
          </>
        )}

        {loggedIn && (
          <ShippingBox>
            <MultipleSelect
              options={[{ id: 1, title: "Abweichende Lieferadresse" }]}
              optionTitleName={"title"}
              compareValue={"id"}
              alignment={"LEFT"}
              onChange={toggleCustomShipping}
            />
            {customShipping && (
              <>
                <AuthText>
                  Diese Adresse gilt nur für diese Bestellung und wird nicht in
                  deinem Profil gespeichert.
                </AuthText>
                {shippingFields.map(({ name, placeholder, maxLength }) => (
                  <StateInput
                    key={name}
                    name={name}
                    forwardedRef={customShipping[name]}
                    onChange={handleShippingChange}
                    error={shippingErrors[name]}
                    type={"text"}
                    placeholder={placeholder}
                    maxLength={maxLength}
                  />
                ))}
              </>
            )}
          </ShippingBox>
        )}

        {!loggedIn && (
          <AuthBox>
            <AuthText>Um ein Abo zu bestellen, melde dich bitte an.</AuthText>
            <AuthLinks>
              <AuthLink href={"/login" + redirectQuery}>Login</AuthLink>
              <AuthLink href={"/register" + redirectQuery}>
                Registrieren
              </AuthLink>
            </AuthLinks>
          </AuthBox>
        )}

        <ButtonBox>
          <StyledButton
            title={"Zahlungspflichtig bestellen"}
            color={colors.YellowBrigthButton}
            scale={"BIG"}
            fontSize={"21px"}
            loading={loading || validating}
            onClick={handleOrder}
          />
        </ButtonBox>

        <AboAccordion
          items={[
            ...(product.description
              ? [{ title: "Beschreibung", html: product.description }]
              : []),
            ...(shippingInfo
              ? [
                  {
                    title: "Versandinformationen",
                    content: <ShippingInfo>{shippingInfo}</ShippingInfo>,
                  },
                ]
              : []),
            // "Stell uns eine Frage" wird im Backend im Feld "Ebene 2 Stell uns eine Frage?" gepflegt
            ...(product.e2_askUsAQuestion?.askusaquestion
              ? [
                  {
                    title: "Stell uns eine Frage",
                    html: product.e2_askUsAQuestion?.askusaquestion,
                  },
                ]
              : []),
          ]}
        />
      </Info>
    </Main>
  );
}

AboProduct.propTypes = {
  product: PropTypes.object.isRequired,
  initialVariationId: PropTypes.number,
  onOrder: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loggedIn: PropTypes.bool,
};

const Main = styled.div`
  ${styl.flexRow};
  align-items: flex-start;
  gap: 80px;
  margin-top: 40px;
  margin-bottom: 60px;
  max-width: 1600px;

  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 30px;
  }
`;

// Galerie bleibt beim Scrollen durch die Beschreibung sichtbar (unter dem Header)
const Gallery = styled.div`
  ${styl.flexColumn};
  flex: 1 1 55%;
  max-width: 850px;
  position: sticky;
  top: 140px;

  @media (max-width: 1100px) {
    position: static;
    width: 100%;
    max-width: 600px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
`;

const Thumbnails = styled.div`
  ${styl.flexRow};
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Thumbnail = styled.img`
  width: 90px;
  height: 90px;
  object-fit: cover;
  cursor: pointer;
  box-sizing: border-box;
  border: 2px solid
    ${(props) => (props.$selected ? colors.OldgoldYellowLogo : "transparent")};

  @media (max-width: ${constants.iPhonePortraitWidth}px) {
    width: 60px;
    height: 60px;
  }
`;

const Info = styled.div`
  ${styl.flexColumn};
  flex: 1 1 45%;
  max-width: 680px;
`;

const Price = styled.p`
  ${styl.boldDefaultFont};
  font-size: 24px;
  color: ${colors.SolidBlackText};
  margin: 20px 0px 0px 0px;
`;

const LabelRow = styled.div`
  ${styl.flexRow};
  align-items: center;
  margin: 25px 0px 8px 0px;
`;

const Label = styled.p`
  ${styl.lightDefaultFont};
  font-size: 14px;
  color: ${colors.MineshaftGreyText};
  margin: 0px;
`;

const Options = styled.div`
  ${styl.flexRow};
  flex-wrap: wrap;
  gap: 10px;
`;

const Option = styled.button`
  ${styl.clear};
  ${styl.lightDefaultFont};
  font-size: 14px;
  padding: 10px 18px;
  cursor: pointer;
  color: ${(props) =>
    props.$selected ? colors.SolidWhiteBackground : colors.SolidBlackText};
  background: ${(props) =>
    props.$selected ? colors.YellowBrigthButton : colors.SolidWhiteBackground};
  ${styl.transition};

  &:hover {
    background: ${(props) =>
      props.$selected
        ? colors.YellowBrigthButton
        : colors.HamptonYellowHomepageSquare};
  }
`;

const AuthBox = styled.div`
  ${styl.flexColumn};
  margin-top: 25px;
`;

const AuthText = styled.p`
  ${styl.lightDefaultFont};
  font-size: 14px;
  color: ${colors.MineshaftGreyText};
  margin: 0px 0px 8px 0px;
`;

const AuthLinks = styled.div`
  ${styl.flexRow};
  flex-wrap: wrap;
  gap: 10px;
`;

const AuthLink = styled.a`
  ${styl.lightDefaultFont};
  font-size: 14px;
  padding: 10px 18px;
  text-decoration: none;
  color: ${colors.SolidBlackText};
  background: ${colors.SolidWhiteBackground};
  ${styl.transition};

  &:hover {
    background: ${colors.HamptonYellowHomepageSquare};
  }
`;

// Eingabefelder und Checkbox heben sich sonst kaum vom grauen Seitenhintergrund ab
const ShippingBox = styled.div`
  ${styl.flexColumn};
  margin-top: 25px;

  input[type="text"] {
    background: ${colors.SolidWhiteBackground};
    color: ${colors.SolidBlackText};
    &::placeholder {
      color: ${colors.SolidGreyText};
    }
  }

  input[type="checkbox"] ~ p {
    color: ${colors.SolidBlackText};
  }

  input[type="checkbox"]:not(:checked) + div {
    background: ${colors.SolidWhiteBackground};
  }
`;

const ShippingInfo = styled.p`
  white-space: pre-line;
`;

const ButtonBox = styled.div`
  margin-top: 30px;
`;
